import React from 'react'
import clsx from 'clsx'
import { format as dateFormat } from 'date-fns'
import { More } from 'iconsax-react'

import MediaAvatar from '../../../../../atoms/MediaAvatar'
import ButtonMenu from '../../../../ButtonMenu'
import Spacer from '../../../../../atoms/Spacer'
import ShowMoreLess from './Collapse'

import {
  replaceMentions,
  loadTaggedUsers,
  formatDateRelative
} from '../../../../../../utilities'
import { useUserAPI } from '../../../../../../providers/UserAPIProvider'
import { useAgent } from '../../../../../../providers/AgentProvider'
import { useCopyableState } from '../../../../../../providers/CopyableStateProvider'
import { useTaskCommentsEdit } from '../../../../../../providers/TaskCommentEditProvider'
import useLongTouch from '../../../../../../hooks/useLongTouch'
import useStyles from './styles'

const COPY_TEXT = 'Copy Text'
const EDIT = 'Edit'
const SAVE = 'Save Changes'
const DISCARD = 'Discard Changes'
const DELETE = 'Delete'

type TaskCommentProps = {
  comment: APIComment
  inModal?: boolean
  disableEdit?: boolean
  onDetails?: boolean
}

const TaskComment: React.FC<TaskCommentProps> = ({
  comment,
  inModal,
  disableEdit,
  onDetails
}) => {
  const { allUsers, loadUser } = useUserAPI()
  const { currentUser } = useAgent()
  const { setText, copyToClipboard } = useCopyableState()
  const { editingID, startEdit, saveEdit, discardEdit, deleteComment } =
    useTaskCommentsEdit()

  const [user, setUser] = React.useState(null)
  const [taggedUsersLoaded, setTaggedUsersLoaded] = React.useState(false)
  const [hovering, setHovering] = React.useState(false)
  const [menuOpen, setMenuOpen] = React.useState(false)

  const { position: mousePosition, ...longTouchProps } = useLongTouch(() => {
    setMenuOpen(true)
    setHovering(false)
  })

  const isYours = React.useMemo(() => {
    return comment.user_id === currentUser()?.id
  }, [comment.user_id, currentUser])

  const canEdit = React.useMemo(() => {
    // Only the author can change the content
    return isYours
  }, [isYours])

  const canDelete = React.useMemo(() => {
    // Only the owner of the comment can delete it
    return isYours
  }, [isYours])

  const classes = useStyles({ inModal, onDetails })

  const { user_id, stamp, content } = comment
  const time = `${dateFormat(
    new Date(stamp),
    'h:mm aaa'
  )}, ${formatDateRelative(stamp, true)}`

  // Reset hovering state
  React.useEffect(() => {
    setHovering(false)
  }, [menuOpen])

  React.useEffect(() => {
    const user = allUsers[user_id]

    if (!user) {
      loadUser(user_id)
      return
    }

    setUser(user)
  }, [allUsers, user_id, loadUser])

  // Loads all tagged users from a message
  React.useEffect(() => {
    if (taggedUsersLoaded) {
      return
    }

    const { content = '' } = comment
    loadTaggedUsers(content, loadUser)

    setTaggedUsersLoaded(true)
  }, [taggedUsersLoaded, comment, loadUser])

  // Stores content for copying
  React.useEffect(() => {
    if (!content) {
      setText('')
    } else {
      const agents = Object.values(allUsers)
      const transformed = replaceMentions(content, agents)
      //const withoutMultipleSpaces = transformed.replace(/ +/gm, ' ')
      setText(transformed)
    }
  }, [content, allUsers, setText])

  // Only a single option for now but can be extended later on
  const options = React.useMemo(() => {
    const opts = [COPY_TEXT]

    if (canEdit && !disableEdit) {
      if (editingID === comment.id) {
        opts.push(SAVE, DISCARD)
      } else {
        opts.push(EDIT)
      }
    }

    if (canDelete) {
      opts.push(DELETE)
    }

    return opts
  }, [canEdit, editingID, comment.id, canDelete, disableEdit])

  const handleMenuSelect = React.useCallback(
    (item: string) => {
      if (item === COPY_TEXT) {
        copyToClipboard()
      } else if (item === EDIT) {
        startEdit(comment)
      } else if (item === SAVE) {
        saveEdit()
      } else if (item === DISCARD) {
        discardEdit()
      } else if (item === DELETE) {
        deleteComment(comment.id)
      }

      setMenuOpen(false)
    },
    [copyToClipboard, startEdit, comment, saveEdit, discardEdit, deleteComment]
  )

  return (
    <div
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      className={clsx(classes.root, isYours && classes.rootYours)}
      {...longTouchProps}
    >
      <MediaAvatar
        user={user}
        className={classes.avatar}
        rootClass={classes.avatarRoot}
      />

      <div className={clsx(classes.comment, isYours && classes.commentYours)}>
        <div
          className={clsx(
            classes.contentHolder,
            isYours && classes.contentHolderYours
          )}
        >
          <div
            className={clsx(classes.content, isYours && classes.contentYours)}
          >
            <ShowMoreLess
              content={content}
              mentionClass={classes.mention}
              allUsers={allUsers}
              me={currentUser()?.name}
              mentionMeClass={classes.mentionYou}
            />
          </div>

          {/* @ts-ignore */}
          <ButtonMenu
            id={`comment-select-${comment.id}`}
            options={options}
            onSelect={handleMenuSelect}
            className={clsx(classes.dots, isYours && classes.dotsYours)}
            manual
            open={menuOpen}
            onOpen={() => setMenuOpen(true)}
            onClose={() => setMenuOpen(false)}
            anchorReference={mousePosition ? 'anchorPosition' : 'anchorEl'}
            anchorPosition={{
              left: (mousePosition?.x ?? 0) - 50,
              top: (mousePosition?.y ?? 0) - 25
            }}
          >
            {!mousePosition && (
              <More
                className='dot'
                style={{
                  transform: 'rotate(90deg)',
                  width: '1rem',
                  height: '1rem',
                  visibility:
                    hovering && options.length > 0 ? 'visible' : 'hidden'
                }}
              />
            )}
          </ButtonMenu>
        </div>

        <div className={clsx(classes.footer, isYours && classes.footerYours)}>
          <div className={classes.name}>
            {user?.name}
            {isYours ? ' (me)' : ''}
          </div>

          <div className={classes.time}>{time}</div>
        </div>
      </div>

      <Spacer />
    </div>
  )
}

export default TaskComment
